import React, { useState, useRef } from 'react'
import {
  Box,
  TextField,
  Typography,
  Button,
  Chip,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  Add as AddIcon,
  Code as CodeIcon,
  Preview as PreviewIcon
} from '@mui/icons-material'

interface TemplateEditorProps {
  initialTemplate?: {
    name: string
    template_type: string
    language: string
    content: string
    variables: string[]
    is_active: boolean
  }
  onSave: (template: any) => void
  onCancel: () => void
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  initialTemplate,
  onSave,
  onCancel
}) => {
  const [name, setName] = useState(initialTemplate?.name || '')
  const [templateType, setTemplateType] = useState(initialTemplate?.template_type || 'whatsapp')
  const [language, setLanguage] = useState(initialTemplate?.language || 'en')
  const [content, setContent] = useState(initialTemplate?.content || '')
  const [isActive, setIsActive] = useState(initialTemplate?.is_active ?? true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const contentRef = useRef<HTMLTextAreaElement>(null)

  // Common template variables
  const commonVariables = [
    { name: 'customer_name', description: 'Customer full name' },
    { name: 'customer_first_name', description: 'Customer first name' },
    { name: 'order_id', description: 'Order ID' },
    { name: 'order_number', description: 'Order number' },
    { name: 'order_total', description: 'Order total amount' },
    { name: 'order_status', description: 'Order status' },
    { name: 'tracking_number', description: 'Shipment tracking number' },
    { name: 'payment_link', description: 'Payment link URL' },
    { name: 'invoice_number', description: 'Invoice number' },
    { name: 'company_name', description: 'Company name' },
    { name: 'support_email', description: 'Support email address' },
    { name: 'support_phone', description: 'Support phone number' }
  ]

  // Extract variables from content
  const extractVariables = (text: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g
    const matches = text.matchAll(regex)
    const variables = new Set<string>()
    
    for (const match of matches) {
      variables.add(match[1].trim())
    }
    
    return Array.from(variables)
  }

  // Insert variable at cursor position
  const insertVariable = (variableName: string) => {
    const textarea = contentRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = content
    const before = text.substring(0, start)
    const after = text.substring(end)
    const variable = `{{${variableName}}}`

    setContent(before + variable + after)

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + variable.length, start + variable.length)
    }, 0)
  }

  // Validate template
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = 'Template name is required'
    }

    if (!content.trim()) {
      newErrors.content = 'Template content is required'
    }

    // Check for unclosed variable placeholders
    const openBraces = (content.match(/\{\{/g) || []).length
    const closeBraces = (content.match(/\}\}/g) || []).length
    if (openBraces !== closeBraces) {
      newErrors.content = 'Template has unclosed variable placeholders'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle save
  const handleSave = () => {
    if (!validate()) return

    const variables = extractVariables(content)

    onSave({
      name,
      template_type: templateType,
      language,
      content,
      variables,
      is_active: isActive
    })
  }

  const detectedVariables = extractVariables(content)

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Template Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>

            <TextField
              fullWidth
              label="Template Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2 }}
              required
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Template Type</InputLabel>
                  <Select
                    value={templateType}
                    label="Template Type"
                    onChange={(e) => setTemplateType(e.target.value)}
                  >
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={language}
                    label="Language"
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="hi">Hindi</MenuItem>
                    <MenuItem value="mr">Marathi</MenuItem>
                    <MenuItem value="gu">Gujarati</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Template Content
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Use <code>{'{{variable_name}}'}</code> syntax to insert variables
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={12}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              error={!!errors.content}
              helperText={errors.content}
              placeholder="Enter your template content here..."
              inputRef={contentRef}
              sx={{ 
                mb: 2,
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '14px'
                }
              }}
              required
            />

            {/* Detected Variables */}
            {detectedVariables.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Detected Variables ({detectedVariables.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {detectedVariables.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      icon={<CodeIcon />}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Validation Errors */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please fix the errors before saving
              </Alert>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave}>
                Save Template
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Variable Palette */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Available Variables
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Click to insert into template
            </Typography>

            <Box sx={{ mt: 2 }}>
              {commonVariables.map((variable) => (
                <Box key={variable.name} sx={{ mb: 1 }}>
                  <Tooltip title={variable.description} placement="left">
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => insertVariable(variable.name)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontFamily: 'monospace'
                      }}
                    >
                      {variable.name}
                    </Button>
                  </Tooltip>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="textSecondary">
              <strong>Syntax:</strong> Use <code>{'{{variable_name}}'}</code> to insert variables.
              Variables will be replaced with actual values when the template is used.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default TemplateEditor
